import { useEffect, useState } from 'react';

import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { type TipId } from '@/tips';
import { cn } from '@/utils/cn';

export interface LeadershipTermOption {
  id: string;
  startYear: number;
  endYear: number | null;
  label: string | null;
  isCurrent: boolean;
}

interface LeadershipTermsManageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  terms: LeadershipTermOption[];
  selectedTermId: string | null;
  title: string;
  emptyMessage: string;
  addTermLabel: string;
  editTermLabel: string;
  cancelLabel: string;
  closeLabel: string;
  tipId?: TipId;
  formatTermLabel: (term: LeadershipTermOption) => string;
  formatTermYears: (term: Pick<LeadershipTermOption, 'startYear' | 'endYear'>) => string;
  onAddTerm: () => void;
  onEditTerm: (termId: string) => void;
}

export const LeadershipTermsManageDialog = ({
  open,
  onOpenChange,
  terms,
  selectedTermId,
  title,
  emptyMessage,
  addTermLabel,
  editTermLabel,
  cancelLabel,
  closeLabel,
  tipId,
  formatTermLabel,
  formatTermYears,
  onAddTerm,
  onEditTerm,
}: LeadershipTermsManageDialogProps) => {
  const [manageTermId, setManageTermId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setManageTermId(selectedTermId ?? terms[0]?.id ?? null);
  }, [open, selectedTermId, terms]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={closeLabel}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-3">
            {tipId ? <Tip tipId={tipId} /> : null}
            {terms.length === 0 ? (
              <p className="text-body-sm text-fg-secondary">{emptyMessage}</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {terms.map((term) => {
                  const isSelected = term.id === manageTermId;
                  return (
                    <li key={term.id}>
                      <button
                        type="button"
                        onClick={() => setManageTermId(term.id)}
                        className={cn(
                          'w-full rounded-lg border px-3 py-2.5 text-left transition-colors',
                          isSelected
                            ? 'border-brand bg-brand/5'
                            : 'border-border bg-surface hover:bg-surface-hover',
                        )}
                      >
                        <span className="block text-body-sm font-medium text-fg">
                          {formatTermLabel(term)}
                        </span>
                        {term.label?.trim() ? (
                          <span className="mt-0.5 block text-caption text-fg-secondary">
                            {formatTermYears(term)}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogBody>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onAddTerm}>
            {addTermLabel}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!manageTermId}
            onClick={() => {
              if (manageTermId) onEditTerm(manageTermId);
            }}
          >
            {editTermLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
